/*
 * lib/hydrate.js
 *
 * Stap 6 — Reads. Laadt alle data uit Supabase en vult `state.S` met
 * dezelfde vorm als de hardcoded seed. Wordt aangeroepen vanuit main.js
 * vóór de eerste render(), nadat de auth-gate is gepasseerd.
 *
 * Mapping-keuze: in de DB heten kolommen `name`, `amount`, `freq_months`,
 * `cat_slug`, etc.; in state.js zijn dat de korte versies `n`, `a`, `f`,
 * `cat`. We mappen heen-en-weer in dit bestand zodat de rest van de
 * codebase geen hertaling hoeft te kennen. Bij elke gemapte rij bewaren
 * we `__id` (de UUID) voor stap 7 (writes).
 */

import { state } from '../state.js';
import {
  fetchProfileFull, fetchHouseholdMembers, fetchHousehold,
  fetchCategories, fetchPersonalExpenses, fetchHouseholdExpenses,
  fetchIncomes, fetchSavingsGoals,
  fetchPersonalExtras, fetchHouseholdExtras,
  fetchWeeklyBudgets,
  updateProfile
} from './supabase.js';

/**
 * Laad de hele app-state uit Supabase voor de ingelogde gebruiker.
 * Stelt ook state.user + state.person in op basis van display_name.
 */
export async function hydrateFromSupabase(session) {
  const meId = session.user.id;

  // 1. Eigen profiel + (optioneel) partner-profiel uit hetzelfde huishouden.
  const myProfile = await fetchProfileFull(meId);
  const householdId = myProfile.household_id;

  // Stash auth-context op state zodat actions.js write-syncs weten welke
  // user_id / household_id naar Supabase te sturen. Alleen gezet bij
  // geslaagde hydrate — bij seed-fallback blijven beide null en sluiten
  // de sync-helpers in actions.js short-circuit af.
  state.userId      = meId;
  state.householdId = householdId;

  // Verse user: app_start_date is nog null. Zet 'm op vandaag zodat
  // "Rest vorige maand"-berekeningen meteen een correcte oorsprong hebben
  // (cycli vóór startdatum tellen niet mee). We muteren myProfile direct
  // zodat mapSettingsFromProfile() hierna de juiste waarde oppikt; de
  // DB-write is fire-and-forget — bij mislukken wordt 't bij de volgende
  // login opnieuw geprobeerd.
  if (!myProfile.app_start_date) {
    const today = new Date().toISOString().slice(0, 10);
    myProfile.app_start_date = today;
    updateProfile(meId, { app_start_date: today }).catch(console.warn);
  }

  // Lang-weg-detectie: als de vorige sessie ≥ 90 dagen geleden was, openen
  // we straks (na render) een popup die vraagt of de startdatum gereset
  // mag worden. We stempelen last_seen_at altijd op nu, fire-and-forget.
  if (myProfile.last_seen_at) {
    const last = new Date(myProfile.last_seen_at).getTime();
    const days = Math.floor((Date.now() - last) / 86_400_000);
    if (days >= 90) {
      state.absencePopupOpen = true;
      state.absenceMonths = Math.floor(days / 30);
    }
  }
  updateProfile(meId, { last_seen_at: new Date().toISOString() }).catch(console.warn);

  // Eerste-keer-tutorial: open zodra de eerste render klaar is wanneer de
  // gebruiker 'm nog niet heeft afgewerkt. Trigger zit in main.js' enterApp.
  state.onboardingOpen = !myProfile.onboarding_done;

  let mateProfile = null;
  if (householdId) {
    const members = await fetchHouseholdMembers(householdId);
    mateProfile = members.find(p => p.id !== meId) || null;
    // Naam + has_shared_account voor de huishouden-sectie. Fire-and-forget;
    // bij fail (RLS race, etc.) blijft de input gewoon leeg.
    fetchHousehold(householdId)
      .then(h => {
        state.householdName     = h?.name || '';
        state.hasSharedAccount  = !!h?.has_shared_account;
      })
      .catch(() => {
        state.householdName     = '';
        state.hasSharedAccount  = false;
      });
  } else {
    state.householdName     = '';
    state.hasSharedAccount  = false;
  }
  // Flag voor de UI (huishouden-sectie in instellingen toont share-checkbox
  // alleen als er écht een medebewoner aangesloten is; anders een hint).
  state.mateConnected = !!mateProfile;

  // 2. Auth-user → 'sven'/'lander' op basis van display_name. Voor twee-
  //    user-setup van Sven+Lander. Generaliseren komt later.
  const meKey   = identityKey(myProfile.display_name);
  const mateKey = meKey === 'sven' ? 'lander' : 'sven';

  state.user   = meKey;
  state.person = meKey;

  // 3. Parallel-fetches voor eigen data + samen-data.
  const [
    myCats, myExpenses, myIncomes, mySavings, myExtras, myWeekBudgets,
    samenExpenses, samenExtras
  ] = await Promise.all([
    fetchCategories(meId),
    fetchPersonalExpenses(meId),
    fetchIncomes(meId),
    fetchSavingsGoals(meId),
    fetchPersonalExtras(meId),
    fetchWeeklyBudgets(meId),
    householdId ? fetchHouseholdExpenses(householdId) : Promise.resolve([]),
    householdId ? fetchHouseholdExtras(householdId)   : Promise.resolve([])
  ]);

  // 4. Partner-data alleen als hij/zij share_with_mate aan heeft staan.
  //    RLS sluit het sowieso af, maar deze check spaart een paar queries
  //    als sharing uit staat.
  let mateExpenses = [], mateIncomes = [], mateSavings = [], mateExtras = [], mateWeekBudgets = [];
  if (mateProfile && mateProfile.share_with_mate) {
    [mateExpenses, mateIncomes, mateSavings, mateExtras, mateWeekBudgets] = await Promise.all([
      fetchPersonalExpenses(mateProfile.id),
      fetchIncomes(mateProfile.id),
      fetchSavingsGoals(mateProfile.id),
      fetchPersonalExtras(mateProfile.id),
      fetchWeeklyBudgets(mateProfile.id)
    ]);
  }

  // 5. Bouw state.S
  state.S.cats     = myCats.map(mapCatFromDb);
  state.S.settings = mapSettingsFromProfile(myProfile);
  state.S[meKey]   = buildPersonState(
    myProfile, myExpenses, myIncomes, mySavings, myExtras, myWeekBudgets
  );

  state.S[mateKey] = mateProfile
    ? buildPersonState(mateProfile, mateExpenses, mateIncomes, mateSavings, mateExtras, mateWeekBudgets)
    : emptyPersonState();

  // 6. Display-namen vanuit auth: meKey/mateKey blijven interne slots ('sven',
  //    'lander') maar de UI moet de échte gebruikersnaam tonen. Fallback op
  //    de slot-naam zodat oude data zonder display_name niet stuk gaat.
  const niceCap = s => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
  state.displayNames = {
    [meKey]:   myProfile.display_name   || niceCap(meKey),
    [mateKey]: (mateProfile?.display_name) || niceCap(mateKey),
    samen:     'Samen'
  };

  state.S.samen = {
    vast: samenExpenses.filter(e => e.scope === 'vast').map(mapExpenseFromDb),
    var:  samenExpenses.filter(e => e.scope === 'var').map(mapExpenseFromDb),
    hist: groupExtrasByHist(samenExtras, buildExpenseLookup(samenExpenses), buildCatLookup(samenExpenses))
  };
}

// ─────────────────────────────────────────────────────────────
// Mapping helpers — DB-row → state.js shape
// ─────────────────────────────────────────────────────────────

function identityKey(displayName) {
  const lc = (displayName || '').toLowerCase();
  if (lc.includes('sven'))   return 'sven';
  if (lc.includes('lander')) return 'lander';
  return 'sven';                 // fallback — me = sven
}

function mapCatFromDb(row) {
  const out = {
    id: row.slug,
    label: row.label,
    emoji: row.emoji,
    color: row.color,
    scope: row.scope
  };
  if (row.locked) out.locked = true;
  return out;
}

function mapSettingsFromProfile(p) {
  return {
    appStartDate:   p.app_start_date  || null,
    mode:           p.theme_mode      || 'light',
    theme:          p.theme_palette   || 'default',
    remainingView:  p.remaining_view  || 'month'
  };
}

function mapExpenseFromDb(row) {
  const fm = row.freq_months;
  const out = {
    __id: row.id,
    n: row.name,
    a: Number(row.amount),
    cat: row.cat_slug
  };
  // Voor freq=1 en freq=12 gebruiken we de legacy 'f'-vorm, anders 'freqM'-getal.
  if (fm === 1)       out.f = 'm';
  else if (fm === 12) out.f = 'j';
  else                out.freqM = fm;

  if (row.color)                  out.c = row.color;
  if (row.auto)                   out.auto = true;
  if (row.domicilie)              out.domicilie = true;
  if (row.betaaldag)              out.betaaldag = row.betaaldag;
  if (row.created_at_cycle)         out.createdAtCycle = row.created_at_cycle;
  if (row.last_paid_cycle)          out.lastPaidCycle = row.last_paid_cycle;
  if (row.abonn_skip_cycle)         out.abonnSkipCycle = row.abonn_skip_cycle;
  if (row.last_used_confirmed_at)   out.lastUsedConfirmedAt = row.last_used_confirmed_at;
  // savings_active default false in DB; bestaande items zonder migratie-
  // applicatie krijgen ook netjes false → "Niet aan het sparen"-UI.
  out.savingsActive = !!row.savings_active;
  if (row.savings_started_at_cycle) out.savingsStartedAtCycle = row.savings_started_at_cycle;
  return out;
}

function mapIncomeFromDb(row) {
  const fm = row.freq_months;
  const out = {
    __id: row.id,
    n: row.name,
    a: Number(row.amount),
    cat: row.cat_slug
  };
  if (fm === 1)       out.f = 'm';
  else if (fm === 12) out.f = 'j';
  else                out.freqM = fm;

  if (row.start_dag) out.startDag = row.start_dag;
  if (row.anker)     out.anker = true;
  return out;
}

function mapSavingsGoalFromDb(row) {
  const out = {
    __id: row.id,
    n: row.name,
    doel: Number(row.doel),
    gesp: Number(row.gespaard),
    pct:  row.pct,
    c:    row.color
  };
  if (row.is_noodfonds) out.isNoodfonds = true;
  if (row.paused)       out.paused = true;
  if (row.deadline)     out.deadline = row.deadline;
  return out;
}

function mapExtraFromDb(row, expenseLookup, catLookup) {
  let dateStr = '';
  if (row.spent_on) {
    const [y, m, d] = row.spent_on.split('-').map(Number);
    dateStr = `${d}/${m}/${y}`;
  }
  const out = {
    __id: row.id,
    a:    Number(row.amount),
    cat:  row.cat_slug,
    desc: row.description || '',
    date: dateStr
  };
  if (row.begroot) out.begroot = true;
  // related_expense_id (UUID) → vrKey ('vast:N' / 'var:N'). De index is
  // niet stabiel in de DB, dus we slaan de UUID op en mappen 'm hier
  // terug naar de huidige positie. Zonder dit gaan de per-item
  // progressie-balkjes leeg na een refresh.
  if (row.related_expense_id && expenseLookup) {
    const ref = expenseLookup[row.related_expense_id];
    if (ref) out.vrKey = `${ref.type}:${ref.index}`;
  }
  // Fallback voor entries die vóór de related_expense_id-fix werden geschreven:
  // koppel op categorie wanneer er precies één gepland item met die cat bestaat.
  // Geen sync — write-pad doet dat zelf bij de volgende edit.
  if (!out.vrKey && row.direction === 'uit' && catLookup) {
    const ref = catLookup[row.cat_slug];
    if (ref) out.vrKey = `${ref.type}:${ref.index}`;
  }
  return out;
}

/** Map UUID → { type:'vast'|'var', index:N } voor de huidige expense-volgorde.
 *  Moet exact dezelfde filter/volgorde gebruiken als state.S[person].vast/var,
 *  anders verwijst vrKey naar het verkeerde item. */
function buildExpenseLookup(expenses) {
  const lookup = {};
  expenses.filter(e => e.scope === 'vast').forEach((r, i) => {
    lookup[r.id] = { type: 'vast', index: i };
  });
  expenses.filter(e => e.scope === 'var').forEach((r, i) => {
    lookup[r.id] = { type: 'var', index: i };
  });
  return lookup;
}

/** Map cat_slug → { type, index } enkel als er precies één gepland item met
 *  die categorie bestaat. Gebruikt als fallback om legacy-entries (zonder
 *  related_expense_id) toch in de juiste progressie-balk te krijgen. */
function buildCatLookup(expenses) {
  const counts = {};
  const first  = {};
  const consider = (scope, type) => {
    expenses.filter(e => e.scope === scope).forEach((r, i) => {
      counts[r.cat_slug] = (counts[r.cat_slug] || 0) + 1;
      if (!first[r.cat_slug]) first[r.cat_slug] = { type, index: i };
    });
  };
  consider('vast', 'vast');
  consider('var',  'var');
  const lookup = {};
  Object.keys(counts).forEach(cat => {
    if (counts[cat] === 1) lookup[cat] = first[cat];
  });
  return lookup;
}

function groupExtrasByHist(rows, expenseLookup, catLookup) {
  const hist = {};
  rows.forEach(r => {
    if (!hist[r.cycle_key]) hist[r.cycle_key] = { ex: [], in: [] };
    const mapped = mapExtraFromDb(r, expenseLookup, catLookup);
    if (r.direction === 'uit') hist[r.cycle_key].ex.push(mapped);
    else                       hist[r.cycle_key].in.push(mapped);
  });
  return hist;
}

function buildPersonState(profile, expenses, incomes, savings, extras, weekBudgets) {
  return {
    shareWithMate: profile.share_with_mate,
    gemAuto:       profile.gem_auto,
    spaarAuto:     profile.spaar_auto,
    gemHandled:    profile.gem_handled    || {},
    spaarHandled:  profile.spaar_handled  || {},
    loonHandled:   profile.loon_handled   || {},
    huidigSaldo:        profile.huidig_saldo == null ? null : Number(profile.huidig_saldo),
    huidigSaldoUpdated: profile.huidig_saldo_updated,
    pensioenActief:     profile.pensioen_actief,
    pensioenGrens:      Number(profile.pensioen_grens) || 1050,
    belastingPct:       Number(profile.belasting_pct)  || 25,
    cycleReviews:       profile.cycle_reviews   || {},
    surplusHandled:     profile.surplus_handled || {},
    schulden:           profile.schulden        || [],
    externeSchulden:    profile.externe_schulden || [],
    // Aanwezig (object met >0 keys) = actieve noodfonds-burst, restore-banner toont.
    noodfondsPctSnapshot: profile.noodfonds_pct_snapshot || null,
    inkomsten:  incomes.map(mapIncomeFromDb),
    vast:       expenses.filter(e => e.scope === 'vast').map(mapExpenseFromDb),
    var:        expenses.filter(e => e.scope === 'var').map(mapExpenseFromDb),
    dromen:     savings.map(mapSavingsGoalFromDb),
    weekBudgets: Object.fromEntries(weekBudgets.map(b => [b.cat_slug, Number(b.amount)])),
    hist:       groupExtrasByHist(extras, buildExpenseLookup(expenses), buildCatLookup(expenses))
  };
}

function emptyPersonState() {
  return {
    shareWithMate: false, gemAuto: false, spaarAuto: false,
    gemHandled: {}, spaarHandled: {},
    huidigSaldo: null, huidigSaldoUpdated: null,
    pensioenActief: false, pensioenGrens: 1050, belastingPct: 25,
    cycleReviews: {}, surplusHandled: {},
    schulden: [], externeSchulden: [],
    inkomsten: [], vast: [], var: [], dromen: [], weekBudgets: {},
    hist: {}
  };
}
